import React, { useContext, useRef, useState, useEffect } from 'react';
import clsx from 'clsx';
import { Element } from '../../element';
import DropdownContext from '../context';
import { setRef } from '../../../../hooks';
import * as styles from '../dropdown.styles';

const threshold = [...Array(50).keys()].map((x) => {
  return x / 50;
});

const dummy = {};

/**
 * @type {typeof import('../').DropdownContent}
 */
const DropdownContent = React.forwardRef(({ children, className, fullWidth = false, style, ...props }, ref) => {
  const { isOpen, isGapless, position = '', dropdownRef, align } = useContext(DropdownContext);
  const [offsets, setOffsets] = useState(dummy);
  const contentRef = useRef(null);

  useEffect(() => {
    const { current } = dropdownRef;
    const content = contentRef?.current;

    if (!current || !content) {
      console.warn('Dropdown.Content: Dropdown Trigger not set or do not support ref forwarding');
      return undefined;
    }

    const calculate = (boundingClientRect) => {
      // TODO: Check another way to not use getBoundingClientRect
      const dropdownRect = current.getBoundingClientRect();

      // calculate the amount of pixels to translate the content (half of the difference between the content and the trigger)
      const delta = (dropdownRect.width - boundingClientRect.width) / 2;

      // if delta make the content go outside the left margin use trigger x position instead to align to the left
      let toTranslate = Math.max((dropdownRect.x - 20) * -1, delta);

      // if delta make the content go outside the right margin use trigger x position instead to align to the right
      toTranslate += Math.min(window.innerWidth - (dropdownRect.x + dropdownRect.width - delta + 20), 0);

      const values = {
        // calculate vertical position
        top: dropdownRect.top - boundingClientRect.height,
        bottom: window.innerHeight - (dropdownRect.bottom + boundingClientRect.height),
        x: window.innerWidth - boundingClientRect.width > 0,
        y: window.innerHeight - boundingClientRect.height > 0,
        // to calculate horizontal position if align is center we need to translate the content
        toTranslate: align === 'center' ? toTranslate : undefined,
        beak: -1 * toTranslate + dropdownRect.width / 2,
      };

      setOffsets(values);
    };
    calculate(content.getBoundingClientRect());

    if (!isOpen) {
      return undefined;
    }

    const observer =
      typeof IntersectionObserver !== 'undefined'
        ? new IntersectionObserver(
            ([{ boundingClientRect }]) => {
              calculate(boundingClientRect);
            },
            { threshold },
          )
        : {
            observe: () => {},
            disconnect: () => {},
          };

    const resize = () => {
      const boundingClientRect = content.getBoundingClientRect();
      calculate(boundingClientRect);
    };

    window.addEventListener('resize', resize);

    observer.observe(content);
    return () => {
      observer.disconnect(content);
      window.removeEventListener('resize', resize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [align, children, isOpen]);

  let calculatedPosition = position || 'bottom';
  if (position.startsWith('auto')) {
    const [, prefered] = position.split('-');
    calculatedPosition = offsets.y && offsets.top > 0 && offsets.bottom < 0 ? 'top' : 'bottom';

    if (prefered === 'top' && offsets.y && offsets.top > 0) {
      calculatedPosition = 'top';
    }
    if (prefered === 'bottom' && offsets.y && offsets.bottom > 0) {
      calculatedPosition = 'bottom';
    }
  }

  return (
    <>
      {!isGapless && (
        <div
          className={clsx(styles.contentGap, {
            [styles[calculatedPosition]]: calculatedPosition,
          })}
        />
      )}
      <Element
        as="div"
        ref={setRef(contentRef, ref)}
        role="dialog"
        className={clsx(
          styles.content,
          {
            [styles.show]: isOpen,
            [styles.gapless]: isGapless,
            [styles.fullWidth]: fullWidth,
            [styles[align]]: align,
            [styles[calculatedPosition]]: calculatedPosition,
          },
          className,
        )}
        style={{
          ...(offsets.toTranslate && {
            transform: `translate(${offsets.toTranslate}px)`,
            left: 0,
            right: 'unset',
            '--tooltip-beak-margin': `${offsets.beak}px`,
          }),
          ...style,
        }}
        {...props}
      >
        {children}
      </Element>
    </>
  );
});

if (process.env.NODE_ENV !== 'production') {
  DropdownContent.displayName = 'Dropdown.Content';
}

export default DropdownContent;
